import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Divider,
  Fade,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { isFunction } from "lodash";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";

/**
 * ResizeableSidebar - Stateless sidebar shell component
 * Handles the sidebar UI, resizing, animations, and backdrop
 * Content is managed by ResizeableSidebarContent
 */
const ResizeableSidebar = ({
  title,
  titleIcon,
  description,
  children,
  onClose,
  open = false,
  hideDivider = false,
  hideBackdrop = false,
  sx,
  headerEndContent,
  bodyPadding = 2,
  defaultWidth = 680,
  bodyPy,
  bodyPx,
  pxSize = 2,
  headerBottomContent,
  headerSx,
  bodySx,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [width, setWidth] = useState(isMobile ? "100%" : defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  const minWidth = 320;
  const maxWidth = isMobile ? "100%" : 1200;

  // const pxSize = isMobile ? 2.4 : 3.4;
  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        if (isFunction(onClose)) {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, dispatch]);

  const handleClose = () => {
    if (isFunction(onClose)) {
      onClose();
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      {hideBackdrop ? null : (
        <Fade in={open}>
          <Box
            onClick={handleClose}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1200,
            }}
          />
        </Fade>
      )}

      {/* Sidebar */}
      <Fade in={open}>
        <Paper
          ref={sidebarRef}
          elevation={8}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: `${width}px`,
            zIndex: 1300,
            display: "flex",
            // borderTopLeftRadius: "10px",
            // borderBottomLeftRadius: "10px",
            borderTopRightRadius: "0px",
            borderBottomRightRadius: "0px",
            flexDirection: "column",
            overflow: "hidden",
            ...sx,
          }}
        >
          {/* Resize Handle with Thumb */}
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "8px",
              cursor: "ew-resize",
              backgroundColor: "transparent",
              display: "flex",

              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                opacity: 1,
                backgroundColor: grey[100],
                ".resize-thumb": {
                  opacity: 1,
                  backgroundColor: grey[400],
                },
              },
              "&:active .resize-thumb": {
                backgroundColor: theme.palette.primary.main,
              },
              transition: "background-color 0.2s",
              zIndex: 1,
            }}
          >
            <Box
              className="resize-thumb"
              sx={{
                width: "2px",
                height: "40px",
                backgroundColor: grey[400],
                // border: `1px solid ${grey[600]}`,
                borderRadius: "4px",
                opacity: 0.5,
                transition: "all 0.2s ease",
                pointerEvents: "none",
              }}
            />

            {/* Resize Thumb Indicator */}
          </Box>

          {/* Header */}
          <Box
            sx={{
              p: 2,
              px: pxSize,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              ...headerSx,
            }}
          >
            <Flx column>
              <Flx ac g={1}>
                {titleIcon}
                <Htag h1 bold>
                  {title}
                </Htag>
              </Flx>
              {description && (
                <Txt sx={{ fontSize: "12px", color: grey[600], mt: 0.5 }}>
                  {description}
                </Txt>
              )}
            </Flx>
            <Flx ac>
              {headerEndContent}
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Flx>
          </Box>
          {headerBottomContent}
          {hideDivider ? null : <Divider sx={{ borderColor: "#eeeeee" }} />}
          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: bodyPadding,
              height: "100%",
              px: bodyPx || pxSize,
              py: bodyPy || bodyPadding,
              overflowY: "auto",
              overflowX: "hidden",
              ...bodySx,
            }}
          >
            {children}
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default ResizeableSidebar;
